import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-D: dual-write helper for the 1-week shadow window.
// sendTrainingMessage writes to BOTH legacy training_messages AND new
// chat_messages via the parent-coach-about-child conversation.
import { sendTrainingMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";

interface Props {
  groupId: string;
  coachId: string;
  childId: string;
  onBack: () => void;
}

export default function ParentMessageThread({ groupId, coachId, childId, onBack }: Props) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["parent-messages", groupId, childId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("training_messages")
        .select("*, profiles:sender_id(full_name)")
        .eq("player_id", childId)
        .eq("group_id", groupId)
        .or(`sender_id.eq.${user!.id},recipient_id.eq.${user!.id}`)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 10000,
  });

  const { data: coachProfile } = useQuery({
    queryKey: ["coach-profile", coachId],
    queryFn: async () => {
      const { data } = await supabase.from("public_profiles" as any).select("full_name").eq("id", coachId).single();
      return data;
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  // UPPDRAG-8 PR-D: dual-write to legacy training_messages + new chat_messages.
  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!text.trim() || !organization || !user) return;
      await sendTrainingMessage(supabase, {
        groupId,
        organizationId: organization.id,
        senderUserId: user.id,
        recipientUserId: coachId,
        childUserId: childId,
        coachUserId: coachId,
        parentUserId: user.id,
        body: text.trim(),
      });
    },
    onSuccess: () => {
      setText("");
      queryClient.invalidateQueries({ queryKey: ["parent-messages", groupId, childId] });
    },
    onError: () => toast({ title: "Kunde inte skicka meddelande", variant: "destructive" }),
  });

  return (
    <div className="flex flex-col h-[70vh]">
      <div className="flex items-center gap-2 pb-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Tillbaka
        </Button>
        <span className="text-sm font-medium">
          Meddelanden till {coachProfile?.full_name || "Tränaren"}
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 px-1 pb-2">
        {isLoading && <p className="text-sm text-muted-foreground">Laddar...</p>}
        {!isLoading && messages.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">Inga meddelanden ännu.</p>
        )}
        {messages.map((m: any) => {
          const isMe = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isMe ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <p>{m.message}</p>
                <p className={`text-[10px] mt-0.5 ${isMe ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {format(parseISO(m.created_at), "d MMM HH:mm", { locale: sv })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-2 border-t">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Skriv meddelande..."
          className="min-h-[40px] max-h-[80px] resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMutation.mutate();
            }
          }}
        />
        <Button size="icon" onClick={() => sendMutation.mutate()} disabled={!text.trim() || sendMutation.isPending}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
